import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { faqs, siteInfo } from '@/mocks/comics';
import { get_form_url } from '@/utils/getFormUrl';

function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '', website_alt: '' });
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = new FormData(form).get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      setCharCount(0);
      return;
    }

    setStatus('idle');
    try {
      const formUrl = await get_form_url({ name: '韩漫屋联系表单' });
      const fd = new FormData(form);
      fd.delete('website_alt');
      const params = new URLSearchParams();
      for (const [key, value] of fd.entries()) {
        params.append(key, value as string);
      }

      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch {}

      if (res.ok && parsed.code === 'OK') {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
        setCharCount(0);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setStatus('error');
        setErrorMsg(msg);
      }
    } catch (_) {
      setStatus('error');
      setErrorMsg('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="" id="contact-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
            placeholder="您的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={e => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
            placeholder="your@email.com"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          value={formData.subject}
          onChange={e => setFormData({ ...formData, subject: e.target.value })}
          className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请选择主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容
          <span className="text-foreground-400 ml-1">({charCount}/{MAX_CHARS})</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={MAX_CHARS}
          value={formData.message}
          onChange={e => {
            setFormData({ ...formData, message: e.target.value });
            setCharCount(e.target.value.length);
          }}
          className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
          placeholder="请输入您的留言内容..."
        ></textarea>
      </div>
      <div className="honeypot-field">
        <input
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          value={formData.website_alt}
          onChange={e => setFormData({ ...formData, website_alt: e.target.value })}
        />
      </div>
      <button
        type="submit"
        className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
      >
        <i className="ri-send-plane-line"></i>
        发送消息
      </button>

      {status === 'success' && (
        <div className="p-4 bg-accent-50 border border-accent-200/50 rounded-lg">
          <p className="text-sm text-accent-800 flex items-center gap-2">
            <i className="ri-checkbox-circle-line"></i>
            感谢您的留言！我们会尽快回复您。
          </p>
        </div>
      )}
      {status === 'error' && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {errorMsg}
          </p>
        </div>
      )}
    </form>
  );
}

export default function AboutPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map(faq => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmanwu.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />
      <main className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">关于韩漫屋</h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                韩漫屋成立于{siteInfo.foundingYear}年，专注于为中文读者提供优质的韩国漫画在线阅读服务。我们致力于打造中国最好的韩漫阅读平台。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-10">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">正版授权</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  与多家韩国漫画平台建立合作，所有漫画均获得正版授权，为创作者和读者建立健康的阅读生态。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                  <i className="ri-translate-2 text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">专业汉化</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  拥有专业的韩语翻译团队，确保每一部漫画的翻译质量，忠实还原原作精髓，无删减呈现给读者。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500">
                  <i className="ri-smartphone-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">极致体验</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  采用下拉式阅读设计，自动适配手机、平板和电脑，无论在何种设备上都能获得流畅的阅读体验。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">读者口碑</h2>
              <p className="text-sm text-foreground-500">来自韩漫爱好者的真实评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map(i => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "韩漫屋真的是我用过最好用的韩漫阅读平台！下拉式阅读太舒服了，翻译质量也很高，完全没有删减。每天必刷！"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 text-xs font-bold">李</div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">李同学</p>
                    <p className="text-xs text-foreground-400">大学生 · 3年读者</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map(i => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "漫画更新速度很快，和韩国几乎同步。分类也很全，想看的韩漫基本都能在这里找到。推荐给所有喜欢韩漫的朋友！"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-500 text-xs font-bold">张</div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">张小姐</p>
                    <p className="text-xs text-foreground-400">上班族 · 2年读者</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map(i => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "从发现韩漫屋开始就一直在用，界面简洁没广告干扰，专注阅读体验。希望平台越来越好，收录更多优质韩漫！"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-500 text-xs font-bold">王</div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">王先生</p>
                    <p className="text-xs text-foreground-400">设计师 · 1年读者</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">常见问题</h2>
              <p className="text-sm text-foreground-500">关于韩漫屋，您可能想了解的问题</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-lg">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">联系我们</h2>
                <p className="text-sm text-foreground-500 leading-relaxed mb-6">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或直接填写右侧的表单，我们会在24小时内回复。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                      <p className="text-sm text-foreground-500">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">公司地址</p>
                      <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">客服电话</p>
                      <p className="text-sm text-foreground-500">{siteInfo.phone}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">发送消息</h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}